// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
import uuid from 'uuid';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import {
	findParentNodeOfTypeClosestToPos,
	findSelectedNodeOfType,
} from '@atlaskit/editor-prosemirror/utils';

import { AGENT_MANAGED_EXTENSION_KEY } from './agentManagedExtensionPluginType';

export const isAgentManagedExtension = (node: PMNode | null | undefined): boolean => {
	if (!node || node.type.name !== 'bodiedExtension') {
		return false;
	}
	const { extensionKey } = node.attrs as {
		extensionKey?: string;
	};
	return extensionKey === AGENT_MANAGED_EXTENSION_KEY;
};

/**
 * Find the agent-managed bodied extension at or around the current selection.
 */
export const findAgentManagedExtension = (
	state: EditorState,
	selection?: Selection | null,
): { node: PMNode; pos: number } | undefined => {
	const { bodiedExtension } = state.schema.nodes;
	if (!bodiedExtension) {
		return undefined;
	}

	const sel = selection || state.selection;

	const selected = findSelectedNodeOfType(bodiedExtension)(sel);
	if (selected && isAgentManagedExtension(selected.node)) {
		return { node: selected.node, pos: selected.pos };
	}

	const parent = findParentNodeOfTypeClosestToPos(sel.$from, bodiedExtension);
	if (parent && isAgentManagedExtension(parent.node)) {
		return { node: parent.node, pos: parent.pos };
	}

	return undefined;
};

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
	return uuid();
};

/** Generate a stable, locally-unique id for a freshly-inserted block. */
export const generateLocalId = (): string => randomId();

/**
 * TODO: CCINT-7833 - replace with a server-issued id once the agent-managed-object
 * 	backend is wired up. For now we mint a random id at insert time so the
 * 	`parameters.aiManagedObjectId` attribute always has a value, even though
 * 	nothing yet hydrates against it.
 */
export const generateAiManagedObjectId = (): string => randomId();
